import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import { Network, Clock, AlertTriangle, MapPin, FileWarning, Link2Off, TrendingDown, Truck } from "lucide-react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { useNavigate } from "react-router-dom";
import {
    UI,
    toneColor,
    priorityTone,
    safeNumber,
    formatMoney,
    shortLabel,
    fetchExchangeRates,
    convertCurrency,
    StatusPill,
    KpiCard,
    KpiGrid,
    Panel,
    ActionList,
    RankList,
    DonutWithLegend,
    MetricRow,
    SegmentedTabs,
    DashboardToolbar,
    barChartOptions,
    ProgressStat,
    TimelineList,
    relativeDays,
    StackedCell,
    DetailsButton,
    TableCard,
    withRawSort,
    dateValue,
    useDateRange,
    downloadCsv,
    csvDate,
    csvMoney,
    PRIORITY_RANK,
} from "../OceanTMS/oceanDashboardUi";
import FileStatusDrawer from "../OceanTMS/FileStatusDrawer";

const safeText = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map((item) => safeText(item, "")).filter(Boolean).join(", ");
    if (typeof value === "object") return value.companyName || value.name || value.label || value.title || value.value || fallback;
    return fallback;
};

const getShipmentRecordId = (shipment) => {
    const attr = shipment?.attributes || shipment || {};
    return shipment?.id || attr?.id || attr?._id || shipment?._id || null;
};

const getRhflNo = (shipment, fallbackIndex = 0) => {
    const attr = shipment?.attributes || shipment || {};
    const no = attr?.shipmentNo || attr?.shipmentNumber || attr?.rhflNo;
    if (no && String(no).startsWith("RHFL")) return String(no);
    return no ? `RHFL${no}` : `RHFL${fallbackIndex + 1}`;
};

const ETA_TONES = {
    Delayed: "danger",
    "ETA Passed": "danger",
    "ETA Risk": "warning",
    "ETA This Week": "info",
    "On Schedule": "success",
    "No ETA": "neutral",
};

const ETA_ORDER = Object.keys(ETA_TONES);

const BREAKDOWNS = [
    { value: "linehaulProvider", label: "Linehaul Provider" },
    { value: "pickupCountry", label: "Pickup Country" },
    { value: "deliveryCountry", label: "Delivery Country" },
    { value: "consolidationType", label: "Consolidation Type" },
    { value: "linkedRhflRange", label: "Linked RHFL" },
    { value: "profitStatus", label: "Profit Status" },
];

const place = (city, country) => [city, country].filter((v) => v && v !== "—").join(", ") || "—";

function RmblDashboard() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [currency, setCurrency] = useState("EUR");
    const [exchangeRates, setExchangeRates] = useState(undefined);
    const { dateRange, setDateRange, inRange } = useDateRange();
    const [tableFilters, setTableFilters] = useState([]);
    const [breakdown, setBreakdown] = useState("linehaulProvider");
    const [trendMode, setTrendMode] = useState("volume");
    const [selectedId, setSelectedId] = useState(null);
    const tableRef = React.useRef(null);

    const handleEditRmbl = (rmblId) => {
        if (!rmblId) return;
        navigate(`/shipping-order/connect-mbl?id=${rmblId}`);
    };

    const handleGoShipmentForm = (shipmentId) => {
        if (!shipmentId) return;
        navigate(`/shipping-order/create-shipment/details?id=${shipmentId}`);
    };

    const filterTitleMap = {
        linehaulProvider: "Linehaul Provider",
        pickupCountry: "Pickup Country",
        deliveryCountry: "Delivery Country",
        etaStatus: "ETA Status",
        etaRiskGroup: "ETA Risk",
        profitStatus: "Profit Status",
        linkedRhflRange: "Linked RHFL",
        missingDocs: "Missing Documents",
        delayed: "Delayed RMBL",
        etaThisWeek: "ETA This Week",
        negativeMargin: "Negative Margin",
        positiveProfit: "Positive Profit",
        consolidationType: "Consolidation Type",
    };

    const setFilter = (type, value, label) => {
        const title = filterTitleMap[type] || "Filter";
        const newFilter = { type, value: value ?? null, label: value ? `${title}: ${label || value}` : title };
        setTableFilters((prev) => {
            const exists = prev.some((f) => f.type === newFilter.type && f.value === newFilter.value);
            if (exists) return prev.filter((f) => !(f.type === newFilter.type && f.value === newFilter.value));
            return [...prev, newFilter];
        });
        setTimeout(() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    };

    const isFilterActive = (type, value = null) =>
        tableFilters.some((f) => f.type === type && (value === null || f.value === value));
    const activeFilterValue = (type) => tableFilters.find((f) => f.type === type)?.value;
    const removeFilter = (filter) =>
        setTableFilters((prev) => prev.filter((f) => !(f.type === filter.type && f.value === filter.value)));
    const clearFilter = () => setTableFilters([]);

    useEffect(() => {
        fetchExchangeRates().then(setExchangeRates);
    }, []);

    useEffect(() => {
        const fetchRmbL = async () => {
            try {
                const service = CrudService.getMBLs || CrudService.getRMBLs || CrudService.getRoadMBLs;
                const res = service ? await service.call(CrudService) : { data: [] };
                const raw = res?.data?.data || res?.data || [];

                const normalized = raw.map((item) => {
                    const a = item.attributes || item;
                    const shipments = Array.isArray(a.shipments) ? a.shipments : [];
                    const financial = a.financialSummary || {};
                    const linehaul = a.linehaulDetails || {};

                    const totalCurrency = financial.totalCurrency || linehaul.currency || a.totalCurrency || "EUR";
                    const totalCost = safeNumber(financial.totalCost);
                    const totalSellingRate = safeNumber(financial.totalSellingRate);
                    const totalProfit = safeNumber(financial.totalProfit || totalSellingRate - totalCost);

                    return {
                        id: item.id || item._id,
                        rmblNo: a.rmblNumber || a.rmblNo || "—",
                        linkedRhflCount: shipments.length,
                        linkedRhflList: shipments.map((shipment, index) => ({
                            id: getShipmentRecordId(shipment),
                            no: getRhflNo(shipment, index),
                        })),
                        linehaulProvider: safeText(linehaul.provider || a.selectedLinehaulProvider || a.serviceProvider),
                        pickupCountry:
                            a.pickupLocation?.country || shipments?.[0]?.attributes?.pickupLocation?.country || "—",
                        pickupCity: a.pickupLocation?.city || "—",
                        deliveryCountry:
                            a.deliveryLocation?.country || shipments?.[0]?.attributes?.deliveryLocation?.country || "—",
                        deliveryCity: a.deliveryLocation?.city || "—",
                        pickupDate: a.pickupDate,
                        cob: a.cob || a.actualDeparture,
                        eta: a.eta || a.estimatedDeliveryDate,
                        originalEtaDate: a.originalEtaDate,
                        aadDate: a.aadDate,
                        delayReason: a.delayReason || "",
                        // LTL RMBLs list their linehaul trucks; FTL ones move on their RHFLs' trucks.
                        truckCount: Array.isArray(a.trucks) && a.trucks.length
                            ? a.trucks.length
                            : shipments.reduce(
                                (sum, s) => sum + Math.max(1, ((s.attributes || s)?.trucks || []).length),
                                0
                            ) || 1,
                        proofOfDelivery: a.proofOfDelivery,
                        plateContainerNumber: a.plateContainerNumber || "—",
                        sealNumber: a.sealNumber || "—",
                        cmrNumber: a.cmrNumber || "—",
                        t1t2: a.t1t2,
                        cmr: a.cmr,
                        linehaulCarrier: a.linehaulCarrier,
                        linehaulCost: safeNumber(linehaul.cost),
                        linehaulCurrency: linehaul.currency || "EUR",
                        totalCost,
                        totalSellingRate,
                        totalProfit,
                        totalCurrency,
                        shipments,
                        grossWeight: shipments.reduce(
                            (sum, s) => sum + safeNumber((s.attributes || s)?.totals?.totalGrossWeight),
                            0
                        ),
                        ldm: shipments.reduce((sum, s) => sum + safeNumber((s.attributes || s)?.totals?.totalLdm), 0),
                    };
                });

                setRows(normalized);
            } catch (error) {
                console.error("RMBL dashboard fetch error:", error);
                setRows([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRmbL();
    }, []);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const dated = rows.filter((row) => inRange(row.cob || row.pickupDate));
        if (!q) return dated;
        return dated.filter((row) =>
            [
                row.rmblNo,
                row.linehaulProvider,
                row.pickupCountry,
                row.pickupCity,
                row.deliveryCountry,
                row.deliveryCity,
                row.plateContainerNumber,
                row.cmrNumber,
                row.sealNumber,
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        );
    }, [rows, search, dateRange]);

    const getConvertedCost = (row) => convertCurrency(row.totalCost, row.totalCurrency, currency, exchangeRates);
    const getConvertedSelling = (row) =>
        convertCurrency(row.totalSellingRate, row.totalCurrency, currency, exchangeRates);
    const getConvertedProfit = (row) => getConvertedSelling(row) - getConvertedCost(row);

    const getProfitStatus = (row) => {
        const profit = getConvertedProfit(row);
        if (profit > 0) return "Profitable";
        if (profit < 0) return "Loss";
        return "Break-even";
    };

    const getEtaStatus = (row) => {
        if (!row.eta) return "No ETA";
        if (row.originalEtaDate && dayjs(row.eta).isAfter(dayjs(row.originalEtaDate), "day")) return "Delayed";
        const daysLeft = dayjs(row.eta).diff(dayjs(), "day");
        if (daysLeft < 0 && !row.proofOfDelivery) return "ETA Passed";
        if (daysLeft <= 3 && daysLeft >= 0) return "ETA Risk";
        if (daysLeft <= 7 && daysLeft >= 0) return "ETA This Week";
        return "On Schedule";
    };

    const getLinkedRhflRange = (row) => {
        if (row.linkedRhflCount === 0) return "No RHFL";
        if (row.linkedRhflCount === 1) return "1 RHFL";
        if (row.linkedRhflCount <= 3) return "2-3 RHFL";
        return "4+ RHFL";
    };

    const getConsolidationType = (row) => {
        if (row.linkedRhflCount <= 1) return "Single RHFL";
        const modes = new Set(
            (row.shipments || [])
                .map((s) => String((s.attributes || s)?.roadTransportType || "").toUpperCase())
                .filter(Boolean)
        );
        const hasLtl = [...modes].some((m) => m.includes("LTL"));
        const hasFtl = [...modes].some((m) => m.includes("FTL"));
        if (hasLtl && hasFtl) return "Mixed Consolidation";
        if (hasLtl) return "LTL Consolidation";
        if (hasFtl) return "FTL Master";
        return "Mixed Consolidation";
    };

    // Dosya alanları dizi olarak saklanır (ör. []); boş dizi de "eksik" sayılmalı
    const isDocMissing = (value) => (Array.isArray(value) ? value.length === 0 : !value);

    const DOCS = [
        ["T1/T2", "t1t2"],
        ["CMR", "cmr"],
        ["Linehaul carrier", "linehaulCarrier"],
    ];

    const getMissingDocs = (row) => DOCS.filter(([, key]) => isDocMissing(row[key])).map(([name]) => name);
    const hasMissingDocs = (row) => getMissingDocs(row).length > 0;

    const isEtaWithinWeek = (row) =>
        row.eta && dayjs(row.eta).diff(dayjs(), "day") >= 0 && dayjs(row.eta).diff(dayjs(), "day") <= 7;

    // Eski RMBL ekranında öncelik yoktu; Ocean MBL ile aynı mantıkla türetildi
    const getPriority = (row) => {
        const etaStatus = getEtaStatus(row);
        if (getConvertedProfit(row) < 0) return "Critical";
        if (etaStatus === "Delayed" || etaStatus === "ETA Passed") return "Critical";
        if (hasMissingDocs(row) || etaStatus === "ETA Risk") return "High";
        if (etaStatus === "ETA This Week") return "Medium";
        return "Normal";
    };

    const applyTableFilter = (data) => {
        if (!tableFilters.length) return data;
        return data.filter((row) =>
            tableFilters.every((filter) => {
                switch (filter.type) {
                    case "linehaulProvider":
                    case "pickupCountry":
                    case "deliveryCountry":
                        return row[filter.type] === filter.value;
                    case "etaStatus":
                        return getEtaStatus(row) === filter.value;
                    case "etaRiskGroup":
                        return ["Delayed", "ETA Passed", "ETA Risk"].includes(getEtaStatus(row));
                    case "profitStatus":
                        return getProfitStatus(row) === filter.value;
                    case "linkedRhflRange":
                        return getLinkedRhflRange(row) === filter.value;
                    case "consolidationType":
                        return getConsolidationType(row) === filter.value;
                    case "missingDocs":
                        return hasMissingDocs(row);
                    case "delayed":
                        return ["Delayed", "ETA Passed"].includes(getEtaStatus(row));
                    case "etaThisWeek":
                        return isEtaWithinWeek(row);
                    case "negativeMargin":
                        return getConvertedProfit(row) < 0;
                    case "positiveProfit":
                        return getConvertedProfit(row) > 0;
                    default:
                        return true;
                }
            })
        );
    };

    const tableRows = useMemo(
        () => applyTableFilter(filteredRows),
        [filteredRows, tableFilters, currency, exchangeRates]
    );

    const kpis = useMemo(() => {
        const totalRmbl = tableRows.length;
        const linkedRhfl = tableRows.reduce((s, r) => s + safeNumber(r.linkedRhflCount), 0);
        const totalCost = tableRows.reduce((s, r) => s + getConvertedCost(r), 0);
        const totalSelling = tableRows.reduce((s, r) => s + getConvertedSelling(r), 0);
        const totalProfit = totalSelling - totalCost;

        return {
            totalRmbl,
            linkedRhfl,
            avgRhfl: totalRmbl ? linkedRhfl / totalRmbl : 0,
            delayed: tableRows.filter((r) => ["Delayed", "ETA Passed"].includes(getEtaStatus(r))).length,
            etaRisk: tableRows.filter((r) => ["Delayed", "ETA Passed", "ETA Risk"].includes(getEtaStatus(r))).length,
            // Filtre ile aynı kural: ETA 0-7 gün içinde
            etaThisWeek: tableRows.filter(isEtaWithinWeek).length,
            missingDocs: tableRows.filter(hasMissingDocs).length,
            noRhfl: tableRows.filter((r) => r.linkedRhflCount === 0).length,
            negativeMargin: tableRows.filter((r) => getConvertedProfit(r) < 0).length,
            totalCost,
            totalSelling,
            totalProfit,
            margin: totalSelling ? (totalProfit / totalSelling) * 100 : 0,
        };
    }, [tableRows, currency, exchangeRates]);

    const groupBy = (getter, { limit = 8, sort = true } = {}) => {
        const map = {};
        filteredRows.forEach((row) => {
            const label = safeText(getter(row), "Unknown");
            map[label] = (map[label] || 0) + 1;
        });
        const list = Object.entries(map).map(([label, count]) => ({ label, count }));
        return sort ? list.sort((a, b) => b.count - a.count).slice(0, limit) : list;
    };

    const etaRows = useMemo(
        () =>
            groupBy(getEtaStatus, { sort: false })
                .sort((a, b) => ETA_ORDER.indexOf(a.label) - ETA_ORDER.indexOf(b.label))
                .map((d) => ({ ...d, color: toneColor(ETA_TONES[d.label]) })),
        [filteredRows]
    );

    const breakdownRows = useMemo(() => {
        switch (breakdown) {
            case "linkedRhflRange":
                return ["No RHFL", "1 RHFL", "2-3 RHFL", "4+ RHFL"].map((label) => ({
                    label,
                    count: filteredRows.filter((r) => getLinkedRhflRange(r) === label).length,
                    color: label === "No RHFL" ? UI.orange : undefined,
                }));
            case "profitStatus":
                return [
                    ["Profitable", "success"],
                    ["Break-even", "neutral"],
                    ["Loss", "danger"],
                ].map(([label, tone]) => ({
                    label,
                    count: filteredRows.filter((r) => getProfitStatus(r) === label).length,
                    color: toneColor(tone),
                }));
            case "consolidationType":
                return groupBy(getConsolidationType);
            default:
                return groupBy((row) => row[breakdown]);
        }
    }, [filteredRows, breakdown, currency, exchangeRates]);

    const monthlyRows = useMemo(() => {
        const map = {};
        filteredRows.forEach((row) => {
            const date = row.cob || row.pickupDate;
            if (!date) return;
            const label = dayjs(date).format("YYYY-MM");
            map[label] = map[label] || { count: 0, profit: 0 };
            map[label].count += 1;
            map[label].profit += getConvertedProfit(row);
        });
        return Object.entries(map)
            .map(([label, v]) => ({ label, ...v }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .slice(-12);
    }, [filteredRows, currency, exchangeRates]);

    const actionItems = [
        { key: "delayed", label: "Delayed RMBL", description: "ETA moved or passed", icon: Clock, tone: "danger", count: kpis.delayed },
        { key: "etaRiskGroup", label: "ETA risk", description: "Delayed, passed or within 3 days", icon: AlertTriangle, tone: "warning", count: kpis.etaRisk },
        { key: "missingDocs", label: "Missing documents", description: "T1/T2, CMR or linehaul carrier missing", icon: FileWarning, tone: "warning", count: kpis.missingDocs },
        { key: "linkedRhflRange", value: "No RHFL", label: "No linked RHFL", description: "Master file without house files", icon: Link2Off, tone: "warning", count: kpis.noRhfl },
        { key: "negativeMargin", value: true, label: "Negative margin", description: "Cost exceeds selling", icon: TrendingDown, tone: "danger", count: kpis.negativeMargin },
    ].map((item) => ({
        ...item,
        active: isFilterActive(item.key, item.value ?? null),
        onClick: () => setFilter(item.key, item.value ?? null),
    }));

    // Önümüzdeki 14 gün içindeki (veya gecikmiş) yükleme ve varış hareketleri
    const upcomingMovements = useMemo(() => {
        const events = [];
        const inWindow = (date, from) => {
            const d = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
            return d >= from && d <= 14;
        };

        tableRows.forEach((row) => {
            const loadDate = row.cob || row.pickupDate;
            if (loadDate && inWindow(loadDate, 0)) events.push({ row, kind: "loading", date: loadDate });
            if (row.eta && !row.proofOfDelivery && inWindow(row.eta, -3)) events.push({ row, kind: "arrival", date: row.eta });
        });

        return events
            .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
            .slice(0, 6)
            .map(({ row, kind, date }) => {
                const overdue = dayjs(date).isBefore(dayjs(), "day");
                return {
                    key: `${row.id}-${kind}`,
                    icon: kind === "loading" ? Truck : MapPin,
                    tone: overdue ? "danger" : kind === "loading" ? "teal" : "info",
                    title: `${row.rmblNo} · ${kind === "loading" ? "Loading" : "Arrival"} ${kind === "loading" ? place(row.pickupCity, row.pickupCountry) : place(row.deliveryCity, row.deliveryCountry)}`,
                    description: `${row.linehaulProvider} · ${row.linkedRhflCount} RHFL`,
                    when: overdue ? `Overdue · ${relativeDays(date)}` : relativeDays(date),
                    whenColor: overdue ? UI.red : UI.text,
                    date: dayjs(date).format("DD MMM"),
                    onClick: () => setSelectedId(row.id),
                };
            });
    }, [tableRows]);

    const monthDelta = useMemo(() => {
        const count = (m) =>
            filteredRows.filter((r) => (r.cob || r.pickupDate) && dayjs(r.cob || r.pickupDate).format("YYYY-MM") === m).length;
        return count(dayjs().format("YYYY-MM")) - count(dayjs().subtract(1, "month").format("YYYY-MM"));
    }, [filteredRows]);

    const withEta = tableRows.filter((r) => r.eta);
    const health = {
        total: tableRows.length,
        docsComplete: tableRows.filter((r) => !hasMissingDocs(r)).length,
        linked: tableRows.filter((r) => r.linkedRhflCount > 0).length,
        etaTotal: withEta.length,
        onSchedule: withEta.filter((r) => ["On Schedule", "ETA This Week", "ETA Risk"].includes(getEtaStatus(r))).length,
        profitable: tableRows.filter((r) => getConvertedProfit(r) > 0).length,
    };

    const getPriorityReasons = (row) => {
        const reasons = [];
        const etaStatus = getEtaStatus(row);
        const missingDocs = getMissingDocs(row);
        if (getConvertedProfit(row) < 0) reasons.push("Negative margin: cost exceeds selling");
        if (etaStatus === "Delayed") {
            reasons.push(`ETA moved from ${dayjs(row.originalEtaDate).format("DD MMM")} to ${dayjs(row.eta).format("DD MMM")}`);
            if (row.delayReason) reasons.push(`Delay reason: ${row.delayReason}`);
        }
        if (etaStatus === "ETA Passed") reasons.push("ETA date has passed without POD");
        if (etaStatus === "ETA Risk") reasons.push("Arrival within 3 days");
        if (etaStatus === "ETA This Week") reasons.push("Arrival within 7 days");
        if (missingDocs.length) reasons.push(`Missing: ${missingDocs.join(", ")}`);
        return reasons.length ? reasons : ["No open issues on this file"];
    };

    // Detay panelinde gösterilecek takvim / evrak / bağlı RHFL / finans özeti
    const buildStatus = (row) => {
        const etaStatus = getEtaStatus(row);
        const priority = getPriority(row);
        const selling = getConvertedSelling(row);
        const cost = getConvertedCost(row);
        const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");
        const loadDate = row.cob || row.pickupDate;

        const steps = [
            {
                label: "Loaded / departed",
                done: Boolean(loadDate) && !dayjs(loadDate).isAfter(dayjs(), "day"),
                date: loadDate ? dayjs(loadDate).format("DD MMM") : null,
            },
            {
                label: "Delivered · POD received",
                done: Boolean(row.proofOfDelivery) && !isDocMissing(row.proofOfDelivery),
                date: row.eta ? `ETA ${dayjs(row.eta).format("DD MMM")}` : null,
            },
        ];
        const currentIndex = steps.findIndex((step) => !step.done);

        return {
            title: row.rmblNo,
            subtitle: row.linehaulProvider !== "—" ? row.linehaulProvider : "No linehaul provider assigned",
            priority,
            pills: [
                { label: etaStatus, tone: ETA_TONES[etaStatus] },
                { label: `${priority} priority`, tone: priorityTone(priority) },
                { label: getConsolidationType(row), tone: "neutral" },
            ],
            facts: [
                ["Origin", place(row.pickupCity, row.pickupCountry)],
                ["Destination", place(row.deliveryCity, row.deliveryCountry)],
                ["Pickup", fmt(row.pickupDate)],
                ["ETA", fmt(row.eta)],
                ["Original ETA", fmt(row.originalEtaDate)],
                ["Plate / container", row.plateContainerNumber],
                ["Trucks", row.truckCount],
                ["AAD", fmt(row.aadDate)],
                ...(row.delayReason ? [["Delay reason", row.delayReason]] : []),
                ["CMR no", row.cmrNumber],
                ["Seal no", row.sealNumber],
            ],
            reasons: getPriorityReasons(row),
            milestonesTitle: "Schedule",
            milestones: steps.map((step, i) => ({ ...step, current: i === currentIndex })),
            checklists: [
                {
                    title: "Documents",
                    progress: true,
                    items: DOCS.map(([name, key]) => ({
                        name,
                        ok: !isDocMissing(row[key]),
                        value: key === "linehaulCarrier" ? "Assigned" : undefined,
                    })),
                },
            ],
            links: {
                title: "Linked RHFL",
                empty: "No RHFL linked to this master file",
                items: row.linkedRhflList.map((s) => ({ label: s.no, onClick: () => handleGoShipmentForm(s.id) })),
            },
            financials: { selling, buying: cost, buyingLabel: "Cost", profit: selling - cost },
            openLabel: "Open RMBL file",
            onOpen: () => handleEditRmbl(row.id),
        };
    };

    const csvColumns = [
        ["RMBL No", (r) => r.rmblNo],
        ["Linked RHFL", (r) => r.linkedRhflList.map((s) => s.no).join(", ")],
        ["Origin", (r) => place(r.pickupCity, r.pickupCountry)],
        ["Destination", (r) => place(r.deliveryCity, r.deliveryCountry)],
        ["Linehaul provider", (r) => r.linehaulProvider],
        ["Plate / container", (r) => r.plateContainerNumber],
        ["Trucks", (r) => r.truckCount],
        ["Delay reason", (r) => r.delayReason],
        ["CMR no", (r) => r.cmrNumber],
        ["Pickup", (r) => csvDate(r.pickupDate)],
        ["COB", (r) => csvDate(r.cob)],
        ["ETA", (r) => csvDate(r.eta)],
        ["ETA status", (r) => getEtaStatus(r)],
        ["Consolidation", (r) => getConsolidationType(r)],
        ["Priority", (r) => getPriority(r)],
        ["Missing documents", (r) => getMissingDocs(r).join(", ")],
        [`Cost (${currency})`, (r) => csvMoney(getConvertedCost(r))],
        [`Selling (${currency})`, (r) => csvMoney(getConvertedSelling(r))],
        [`Profit (${currency})`, (r) => csvMoney(getConvertedProfit(r))],
    ];

    const selectedRow = rows.find((r) => r.id === selectedId);

    const linkSx = {
        color: UI.blue,
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 13,
        "&:hover": { textDecoration: "underline" },
    };

    const table = {
        columns: withRawSort([
            { Header: "RMBL No", accessor: "rmblNo" },
            { Header: "Linked RHFL", accessor: "linkedRhflCount" },
            { Header: "Origin / Destination", accessor: "route" },
            { Header: "Provider / Plate", accessor: "linehaulProvider" },
            { Header: "ETA", accessor: "eta" },
            { Header: "Cost", accessor: "totalCost" },
            { Header: "Selling", accessor: "totalSellingRate" },
            { Header: "Profit", accessor: "totalProfit" },
            { Header: "Status", accessor: "status" },
        ]),
        rows: tableRows.map((row) => {
            const cost = getConvertedCost(row);
            const selling = getConvertedSelling(row);
            const profit = selling - cost;
            const margin = selling ? (profit / selling) * 100 : 0;
            const priority = getPriority(row);

            return {
                _sort: {
                    rmblNo: row.rmblNo,
                    linkedRhflCount: row.linkedRhflCount,
                    route: place(row.pickupCity, row.pickupCountry),
                    linehaulProvider: row.linehaulProvider,
                    eta: dateValue(row.eta),
                    totalCost: cost,
                    totalSellingRate: selling,
                    totalProfit: profit,
                    status: PRIORITY_RANK[priority],
                },
                rmblNo: (
                    <MDTypography component="span" sx={linkSx} onClick={() => handleEditRmbl(row.id)}>
                        {row.rmblNo}
                    </MDTypography>
                ),
                linkedRhflCount: row.linkedRhflList.length ? (
                    <MDBox display="flex" flexDirection="column" gap={0.25}>
                        {row.linkedRhflList.map((s, index) => (
                            <MDTypography
                                key={`${s.id || "rhfl"}-${index}`}
                                component="span"
                                sx={{ ...linkSx, fontSize: 12.5, display: "block" }}
                                onClick={() => handleGoShipmentForm(s.id)}
                            >
                                {s.no}
                            </MDTypography>
                        ))}
                    </MDBox>
                ) : (
                    <StatusPill label="No RHFL" tone="warning" />
                ),
                route: (
                    <StackedCell
                        lines={[
                            ["Origin", place(row.pickupCity, row.pickupCountry)],
                            ["Destination", place(row.deliveryCity, row.deliveryCountry)],
                        ]}
                    />
                ),
                linehaulProvider: (
                    <StackedCell
                        labelWidth={58}
                        lines={[
                            ["Provider", row.linehaulProvider],
                            ["Plate", row.plateContainerNumber],
                        ]}
                    />
                ),
                eta: row.eta ? dayjs(row.eta).format("DD/MM/YYYY") : "—",
                totalCost: formatMoney(cost, currency),
                totalSellingRate: formatMoney(selling, currency),
                totalProfit: (
                    <MDBox>
                        <MDTypography
                            component="div"
                            sx={{ fontSize: 13, fontWeight: 700, color: profit > 0 ? UI.green : profit < 0 ? UI.red : UI.muted }}
                        >
                            {formatMoney(profit, currency)}
                        </MDTypography>
                        <MDTypography component="div" sx={{ fontSize: 11.5, color: UI.muted, lineHeight: 1.4 }}>
                            {margin.toFixed(1)}% margin
                        </MDTypography>
                    </MDBox>
                ),
                status: (
                    <DetailsButton
                        priority={priority}
                        hint={`${getEtaStatus(row)} · ${priority} priority`}
                        onClick={() => setSelectedId(row.id)}
                    />
                ),
            };
        }),
    };

    if (loading) {
        return (
            <MDBox height="50vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </MDBox>
        );
    }

    return (
        <MDBox display="flex" flexDirection="column" gap={2}>
            <DashboardToolbar
                search={search}
                onSearch={setSearch}
                placeholder="Search RMBL, provider, pickup, delivery, plate, CMR, seal..."
                currency={currency}
                onCurrency={setCurrency}
                filters={tableFilters}
                onRemoveFilter={removeFilter}
                onClear={clearFilter}
                dateRange={dateRange}
                onDateRange={setDateRange}
                dateLabel="Loading"
                ratesMissing={exchangeRates === null}
                resultLabel={`${tableRows.length} RMBL files`}
            />

            <KpiGrid>
                <KpiCard
                    label="Master Files"
                    value={kpis.totalRmbl}
                    icon={Network}
                    tone="neutral"
                    trend={{ delta: monthDelta, label: `loaded this month vs last · avg ${kpis.avgRhfl.toFixed(1)} RHFL` }}
                    onClick={clearFilter}
                />
                <KpiCard
                    label="Delayed"
                    value={kpis.delayed}
                    hint="ETA moved or passed"
                    icon={Clock}
                    tone="danger"
                    active={isFilterActive("delayed")}
                    onClick={() => setFilter("delayed")}
                />
                <KpiCard
                    label="ETA Risk"
                    value={kpis.etaRisk}
                    hint="Delayed, passed or ≤ 3 days"
                    icon={AlertTriangle}
                    tone="warning"
                    active={isFilterActive("etaRiskGroup")}
                    onClick={() => setFilter("etaRiskGroup")}
                />
                <KpiCard
                    label="Arriving ≤ 7 Days"
                    value={kpis.etaThisWeek}
                    hint="ETA within the next week"
                    icon={MapPin}
                    tone="info"
                    active={isFilterActive("etaThisWeek")}
                    onClick={() => setFilter("etaThisWeek")}
                />
                <KpiCard
                    label="Missing Documents"
                    value={kpis.missingDocs}
                    hint="T1/T2, CMR or linehaul carrier"
                    icon={FileWarning}
                    tone="warning"
                    active={isFilterActive("missingDocs")}
                    onClick={() => setFilter("missingDocs")}
                />
            </KpiGrid>

            <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                    <Panel title="ETA Status" subtitle="Click a status to filter the table">
                        <DonutWithLegend
                            data={etaRows}
                            totalLabel="RMBL"
                            activeValue={activeFilterValue("etaStatus")}
                            onSelect={(label) => setFilter("etaStatus", label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Action Center" subtitle="Items needing operational follow-up" bodySx={{ py: 1 }}>
                        <ActionList items={actionItems} />
                    </Panel>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Panel title="Financial Summary" subtitle={`Filtered RMBL files · ${currency}`}>
                        <MetricRow label="Selling" value={formatMoney(kpis.totalSelling, currency)} />
                        <MetricRow label="Cost" value={formatMoney(kpis.totalCost, currency)} />
                        <MetricRow
                            label="Gross Profit"
                            value={formatMoney(kpis.totalProfit, currency)}
                            valueColor={kpis.totalProfit >= 0 ? UI.green : UI.red}
                            strong
                        />
                        <MetricRow
                            label="Margin"
                            value={`${kpis.margin.toFixed(1)}%`}
                            valueColor={kpis.margin >= 0 ? UI.text : UI.red}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel
                        title="Upcoming Movements"
                        subtitle="Loadings and arrivals in the next 14 days · click for details"
                        bodySx={{ py: 1 }}
                    >
                        <TimelineList items={upcomingMovements} emptyText="No loadings or arrivals in the next 14 days" />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel title="Operational Health" subtitle="Share of RMBL files in view">
                        <MDBox display="flex" flexDirection="column" gap={2}>
                            <ProgressStat
                                label="Documents complete"
                                value={health.docsComplete}
                                total={health.total}
                                tone={health.docsComplete / (health.total || 1) >= 0.8 ? "success" : "warning"}
                                onClick={() => setFilter("missingDocs")}
                            />
                            <ProgressStat label="Linked to RHFL" value={health.linked} total={health.total} tone="teal" />
                            <ProgressStat
                                label="ETA on schedule (files with ETA)"
                                value={health.onSchedule}
                                total={health.etaTotal}
                                tone="info"
                            />
                            <ProgressStat label="Profitable" value={health.profitable} total={health.total} tone="success" />
                        </MDBox>
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Panel title="Breakdown" subtitle="Top 8 · click a row to filter the table">
                        <MDBox mb={2}>
                            <SegmentedTabs options={BREAKDOWNS} value={breakdown} onChange={setBreakdown} />
                        </MDBox>
                        <RankList
                            data={breakdownRows}
                            activeValue={activeFilterValue(breakdown)}
                            onSelect={(label) => setFilter(breakdown, label)}
                        />
                    </Panel>
                </Grid>

                <Grid item xs={12} lg={5}>
                    <Panel
                        title="Monthly Trend"
                        subtitle="By loading month (last 12)"
                        action={
                            <SegmentedTabs
                                options={[
                                    { value: "volume", label: "Volume" },
                                    { value: "profit", label: "Profit" },
                                ]}
                                value={trendMode}
                                onChange={setTrendMode}
                            />
                        }
                    >
                        <MDBox height={300}>
                            <Bar
                                data={{
                                    labels: monthlyRows.map((x) => dayjs(`${x.label}-01`).format("MMM YY")),
                                    datasets: [
                                        trendMode === "volume"
                                            ? {
                                                label: "RMBL files",
                                                data: monthlyRows.map((x) => x.count),
                                                backgroundColor: UI.teal,
                                                borderRadius: 4,
                                                maxBarThickness: 28,
                                            }
                                            : {
                                                label: `Profit (${currency})`,
                                                data: monthlyRows.map((x) => Number(x.profit.toFixed(2))),
                                                backgroundColor: monthlyRows.map((x) => (x.profit >= 0 ? UI.green : UI.red)),
                                                borderRadius: 4,
                                                maxBarThickness: 28,
                                            },
                                    ],
                                }}
                                options={barChartOptions}
                            />
                        </MDBox>
                    </Panel>
                </Grid>
            </Grid>

            <TableCard
                cardRef={tableRef}
                onExport={() => downloadCsv("RMBL-files", csvColumns, tableRows)}
                title="RMBL File Control"
                summary={
                    tableFilters.length
                        ? `${tableRows.length} of ${filteredRows.length} RMBL files · ${tableFilters.map((f) => shortLabel(f.label, 40)).join(", ")}`
                        : `All RMBL files · ${tableRows.length}`
                }
            >
                <DataTable
                    table={table}
                    entriesPerPage={{ defaultValue: 25, entries: [25, 50, 100] }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                />
            </TableCard>

            <FileStatusDrawer
                status={selectedRow ? buildStatus(selectedRow) : null}
                currency={currency}
                onClose={() => setSelectedId(null)}
            />
        </MDBox>
    );
}

export default RmblDashboard;
